import { Icon, Select } from '@components';
import { content } from '@content';
import { Box, Button, InputBase, Menu, MenuItem, Tooltip, debounce } from '@material-ui/core';
import { SignalsSearchItem, attributeLibrary } from '@modules';
import { HTMLElementWithDocument, TreatmentBuilderContext } from '@routes';
import { Attribute } from '@routes/SignalBuilder/Attribute';
import { SEARCH_ATTRIBUTE_DELAY } from '@routes/SignalBuilder/DataAttributeLibrary/DataAttributeLibrary.const';
import { useAppDispatch } from '@store';
import { format, useHistory } from '@utils';
import React, { ChangeEvent, MouseEvent, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { FORMAT_OPTIONS, PROFILE_ATTRIBUTES } from './AddSignalDropdown.const';
import { getChosenElem, rewriteChosenElementContent } from './AddSignalDropdown.helpers';
import { AddSignalDropdownProps, TPersonalizationToken } from './AddSignalDropdown.props';
import { useStyles, useTooltipStyles } from './AddSignalDropdown.styles';
import SelectDataFormat from './SelectDataFormat/SelectDataFormat';
import { variables } from '@styles';
import { SubjectLinePreheaderContext } from '../../SubjectLinePreheaderContext';

import { PERSONALIZATION_TOKEN_TAG } from '@utils/personalization';

export const AddSignalDropdown = () => {
  const dispatch = useAppDispatch();
  const { businessUnit } = useHistory().query;
  const styles = useStyles();
  const tooltipStyles = useTooltipStyles();
  const { items: attributes } = attributeLibrary.useListData();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedSignals, setSelectedSignals] = useState<TPersonalizationToken[]>([]);

  const { targetInputFieldId, updateSignalList, onSignalDelete, onSignalInsert, onSignalUpdate, setUpdateSignalList } =
    useContext(SubjectLinePreheaderContext);

  // pull the list of attributes from the store and filter out already selected signals
  const filteredAttributes = useMemo(
    () =>
      PROFILE_ATTRIBUTES.concat(attributes).filter(
        (attribute) =>
          !selectedSignals.map((signal) => signal.label).includes(attribute.label) &&
          ['textual', 'numeric'].includes(attribute.type),
      ),
    // [PROFILE_ATTRIBUTES, attributes, selectedSignals],
    [attributes, selectedSignals],
  );

  const { chosenModuleElemId, cursorPosition, setCursorPosition, setIsModuleChanged } =
    useContext(TreatmentBuilderContext);

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const runSearch = useMemo(
    () =>
      debounce((keyword: string) => {
        dispatch(
          attributeLibrary.thunk.search({
            businessUnit: businessUnit!,
            keyword,
          }),
        );
      }, SEARCH_ATTRIBUTE_DELAY),
    [dispatch, businessUnit],
  );

  // update the list of selected ptokens when the target input field changes
  const updateSelectedSignals = useCallback(() => {
    if (targetInputFieldId) {
      const chosenElem = document.getElementById(targetInputFieldId);
      const curSignals: any = [];

      const potentialTokens = chosenElem?.querySelectorAll(PERSONALIZATION_TOKEN_TAG);

      potentialTokens?.forEach((potentialToken) => {
        curSignals.push({
          name: potentialToken.getAttribute('data-attribute-name') || '',
          label: potentialToken.getAttribute('initialValue') || '',
          format: (potentialToken.getAttribute('initialValue') || '').includes('|')
            ? (potentialToken.getAttribute('initialValue') || '').split('|')[1].trim()
            : 'NO_DATA',
        });
      });

      if (curSignals && curSignals.length) {
        setSelectedSignals(
          curSignals.map((signalLabel: any) => {
            return {
              label: signalLabel.label,
              format: signalLabel.format ?? 'NO_DATA',
              name: signalLabel.name,
              isNew: false,
              isDropdownShown: true,
            } as TPersonalizationToken;
          }),
        );
      } else {
        setSelectedSignals([]);
      }
    }
  }, [targetInputFieldId]);

  const handleSearchChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      runSearch(event.target.value);
    },
    [runSearch],
  );

  const handleSignalClick = (signal: SignalsSearchItem) => {
    if (targetInputFieldId) {
      const chosenElem = document.getElementById(targetInputFieldId) as HTMLDivElement;
      // const signalBody = ` <${PERSONALIZATION_TOKEN_TAG} data-attribute-name="${signal.name}" initialValue="${signal.label}" contenteditable="false" style="border-radius: 3px; color: ${variables.colorV2.personalizationToken.text}; background-color: ${variables.colorV2.personalizationToken.background}; pointer-events:none; border-radius: 7px; padding: 0 10px;">${signal.label}</${PERSONALIZATION_TOKEN_TAG}> `;
      const signalBody = ` <${PERSONALIZATION_TOKEN_TAG} data-attribute-name="${signal.name}" initialValue="${signal.label}" contenteditable="false">${signal.label}</${PERSONALIZATION_TOKEN_TAG}> `;

      if (chosenElem) {
        setSelectedSignals((prev) => {
          return [
            {
              label: signal.label,
              name: signal.name,
              signalType: signal.signalType,
              totalSubscribers: signal.totalSubscribers,
              updatedAt: signal.updatedAt,
              type: signal.type,
              isNew: true,
              isDropdownShown: false,
            },
            ...prev,
          ];
        });
        setTimeout(() => {
          setSelectedSignals((prev) =>
            prev.map((item) => ({
              ...item,
              isDropdownShown: true,
            })),
          );
        });
        rewriteChosenElementContent(chosenElem, cursorPosition, signalBody, chosenModuleElemId, onSignalInsert);

        onSignalInsert(chosenElem, signalBody);

        const tokens = chosenElem.querySelectorAll(PERSONALIZATION_TOKEN_TAG);

        Array.from(tokens).forEach((token: any) => {
          const observer = new MutationObserver((mutationList) =>
            mutationList
              .filter((m) => m.type === 'childList')
              .forEach((m) => {
                m.addedNodes.forEach(() => {
                  if (m.target.parentElement && m.target.parentElement.getAttribute('initialValue')) {
                    m.target.parentElement.innerHTML = m.target.parentElement.getAttribute('initialValue') ?? '';
                  }
                });
              }),
          );
          observer.observe(token, { childList: true, subtree: true });
        });

        setTimeout(() => {
          chosenElem.focus();
        });

        handleClose();
        setIsModuleChanged(true);
      }
    }
  };

  const handleRemoveSignal = (signal: TPersonalizationToken) => () => {
    // get the current input field
    const selectedElement = document?.getElementById(targetInputFieldId || '') as HTMLDivElement;

    // get the personalization token html element for this signal
    const tokenElement = selectedElement?.querySelector(
      `${PERSONALIZATION_TOKEN_TAG}[data-attribute-name="${signal.name}"]`,
    );

    // if it exists, remove it from the input field html
    if (tokenElement) {
      tokenElement.remove();
    }

    // update the list of selected signals/tokens
    if (!signal.isNew) {
      setSelectedSignals((prev) => prev.filter((item) => item.label !== signal.label));
    }

    // call onSignalDelete context function
    const updatedSignalHtml = selectedElement?.innerHTML;
    onSignalDelete(updatedSignalHtml);

    // focus back on the input field
    // setTimeout(() => {
    //   selectedElement?.focus();
    // });
  };

  // const handleFormatChange = (signal: TPersonalizationToken) => (e: any) => {
  const handleFormatChange = (signal: TPersonalizationToken) => {
    const chosenElem = getChosenElem(targetInputFieldId);

    // select PTOKEN
    const signalElement = chosenElem?.querySelector(
      `${PERSONALIZATION_TOKEN_TAG}[data-attribute-name="${signal.name}"]`,
    );

    // Update data format values
    if (signalElement) {
      const labelWithoutFormat = signal.label.includes('|') ? signal.label.split('|')[0].trim() : signal.label;

      const initialValue =
        signal.format === 'NO_DATA' ? labelWithoutFormat : `${labelWithoutFormat} | ${signal.format}`;
      const innerHtml = signal.format === 'NO_DATA' ? labelWithoutFormat : `${labelWithoutFormat} | ${signal.format}`;
      signalElement.setAttribute('initialValue', initialValue);
      signalElement.innerHTML = innerHtml;

      setIsModuleChanged(true);
      setCursorPosition(chosenElem.innerHTML.length);

      setTimeout(() => {
        chosenElem.focus();
      });

      onSignalUpdate(chosenElem.innerHTML);
      updateSelectedSignals();
    }
  };

  useEffect(() => {
    runSearch('');
  }, [runSearch]);

  // update the list of selected ptokens when the target input field changes
  useEffect(() => {
    updateSelectedSignals();
    // kep the following targetInputFieldId dependency
  }, [targetInputFieldId, updateSelectedSignals]);

  // update the list of selected ptokens when updateSignalList changes to true
  useEffect(() => {
    if (updateSignalList) {
      // Perform actions when updateSignalList is true
      updateSelectedSignals();
      setUpdateSignalList(false);
    }
  }, [setUpdateSignalList, updateSelectedSignals, updateSignalList]);

  return (
    <Box className={styles.addSignalDropdown}>
      <Box className={styles.signalsWrapper}>
        {selectedSignals.map((signal, index) => (
          <React.Fragment key={`selected-signal-${index}`}>
            <Box className={styles.editingItem}>
              <div className={styles.personalizationToken}>
                <Tooltip
                  title={
                    signal.updatedAt
                      ? signal.signalType === 'foreign'
                        ? `${format.date(signal.updatedAt, 'MM/dd/yy')}`
                        : `${format.date(signal.updatedAt, 'MM/dd/yy')} | ${signal.type} | ${format.number(
                            signal.totalSubscribers,
                          )}`
                      : ''
                  }
                  placement="bottom"
                  arrow
                  classes={tooltipStyles}
                >
                  <Box className={`${styles.editingSignal} ${styles.signalCapsule} signal-item`}>
                    <p className={styles.signalText}>{signal?.label}</p>
                  </Box>
                </Tooltip>

                <SelectDataFormat signal={signal} onClick={handleFormatChange} />

                <Button
                  className={styles.removeBtn}
                  variant="contained"
                  color="primary"
                  onClick={handleRemoveSignal({ ...signal, isNew: false })}
                >
                  <Icon.TrashOutline width={'16px'} height={'16px'} />
                </Button>
              </div>
            </Box>
          </React.Fragment>
        ))}
      </Box>

      <Button
        id="add-signal-btn"
        className={`${styles.addButton} ${anchorEl ? 'active' : ''}`}
        variant="outlined"
        color="primary"
        onClick={handleClick}
        data-opened={Boolean(anchorEl)}
      >
        <Icon.Add fill={variables.color.primary.darkestGray} height="20" width="20" />
      </Button>
      <Menu
        elevation={0}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        keepMounted
        onClose={handleClose}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          className: styles.menuList,
        }}
      >
        <InputBase
          className={styles.search}
          placeholder={content.search}
          onChange={handleSearchChange}
          endAdornment={<Icon.SearchOutline />}
        />
        {filteredAttributes.map((attribute, index) => (
          <Attribute
            key={`attribute-${index}`}
            className={''}
            item={attribute}
            dateOnly={true}
            isCloudShown={attribute.signalType === 'foreign'}
            isClickable={true}
            onClick={handleSignalClick}
          />
        ))}
      </Menu>
    </Box>
  );
};

AddSignalDropdown.displayName = 'AddSignalDropdown';
